import React, { useState, useEffect } from 'react';
import { Search, Plus, RefreshCw, Edit, AlertTriangle, Clock, CheckCircle2, XCircle, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappings
const INCIDENT_PRIORITIES = {
  critical: { label: '🔴 Critical', color: 'destructive' },
  high: { label: '🟠 High', color: 'orange' },
  medium: { label: '🟡 Medium', color: 'yellow' },
  low: { label: '🔵 Low', color: 'blue' }
};

const INCIDENT_STATUSES = {
  new: { label: 'New', color: 'default' },
  investigating: { label: 'Investigating', color: 'blue' },
  confirmed: { label: 'Confirmed', color: 'purple' },
  planned: { label: 'Planned', color: 'indigo' },
  in_progress: { label: 'In Progress', color: 'yellow' },
  testing: { label: 'Testing', color: 'cyan' },
  resolved: { label: 'Resolved', color: 'green' },
  closed: { label: 'Closed', color: 'secondary' }
};

const INCIDENT_CATEGORIES = [
  'Auth', 'Backend', 'Frontend', 'IA', 'Images', 'Performance',
  'Sécurité', 'UX', 'Infrastructure', 'API', 'Database', 'Autre'
];

const IncidentManager = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: ''
  });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    reproduction_steps: '',
    expected_result: '',
    observed_result: ''
  });
  const [updateData, setUpdateData] = useState({
    status: '',
    assigned_to: '',
    admin_notes: '',
    status_comment: ''
  });

  // Fetch incidents
  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/admin/beta/incidents?${params.toString()}`, {
        withCredentials: true
      });

      setIncidents(response.data.incidents);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      toast.error('Erreur lors du chargement des incidents');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/beta/incidents-stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchIncidents();
      fetchStats();
      
      // Check if coming from feedback manager
      const feedbackData = sessionStorage.getItem('create_incident_from_feedback');
      if (feedbackData) {
        try {
          const data = JSON.parse(feedbackData);
          setNewIncident({
            title: data.title || '',
            description: data.description || '',
            category: data.category || '',
            priority: data.priority || 'medium',
            reproduction_steps: '',
            expected_result: '',
            observed_result: ''
          });
          setCreateModalOpen(true);
          sessionStorage.removeItem('create_incident_from_feedback');
          toast.info('Incident pré-rempli depuis le feedback');
        } catch (error) {
          console.error('Failed to parse feedback data:', error);
        }
      }
    }
  }, [user, filters]);

  // Open detail modal
  const handleViewDetails = async (incident) => {
    try {
      const response = await axios.get(`${API}/admin/beta/incidents/${incident.id}`, {
        withCredentials: true
      });
      setSelectedIncident(response.data);
      setUpdateData({
        status: response.data.status,
        assigned_to: response.data.assigned_to || '',
        admin_notes: response.data.admin_notes || '',
        status_comment: ''
      });
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch incident details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Create incident
  const handleCreate = async () => {
    if (!newIncident.title || !newIncident.description || !newIncident.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/admin/beta/incidents`, newIncident, {
        withCredentials: true
      });

      toast.success('Incident créé avec succès');
      setCreateModalOpen(false);
      setNewIncident({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        reproduction_steps: '',
        expected_result: '',
        observed_result: ''
      });
      fetchIncidents();
      fetchStats();
    } catch (error) {
      console.error('Failed to create incident:', error);
      toast.error('Erreur lors de la création');
    }
  };

  // Update incident
  const handleUpdate = async () => {
    if (!selectedIncident) return;

    try {
      await axios.patch(
        `${API}/admin/beta/incidents/${selectedIncident.id}`,
        updateData,
        { withCredentials: true }
      );

      toast.success('Incident mis à jour avec succès');
      setDetailModalOpen(false);
      fetchIncidents();
      fetchStats();
    } catch (error) {
      console.error('Failed to update incident:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Accès réservé aux administrateurs
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Incident Manager</h1>
          <p className="text-muted-foreground mt-2">
            Gestion centralisée des incidents Closed Beta
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Incident
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Ouverts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.open_incidents}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                Critiques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">{stats.critical_incidents}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Résolus
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats.resolved_incidents}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Temps moy.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.avg_resolution_time_hours}h</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Category filter */}
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters({ ...filters, category: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {INCIDENT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Priority filter */}
            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters({ ...filters, priority: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                {Object.entries(INCIDENT_PRIORITIES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(INCIDENT_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" size="sm" onClick={fetchIncidents}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Incidents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Incidents ({incidents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Chargement...</p>
          ) : incidents.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Aucun incident trouvé
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Titre</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Origine</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.map((incident) => (
                    <TableRow key={incident.id} className={incident.priority === 'critical' ? 'bg-red-50 dark:bg-red-950/10' : ''}>
                      <TableCell>
                        <Badge variant={INCIDENT_PRIORITIES[incident.priority]?.color || 'default'}>
                          {INCIDENT_PRIORITIES[incident.priority]?.label || incident.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="font-medium">{incident.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {incident.description.substring(0, 80)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{incident.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={INCIDENT_STATUSES[incident.status]?.color || 'default'}>
                          {INCIDENT_STATUSES[incident.status]?.label || incident.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{incident.origin}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(incident.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(incident)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Incident Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouvel incident</DialogTitle>
            <DialogDescription>
              Documenter un problème détecté pendant la Closed Beta
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="create-title">Titre *</Label>
              <Input
                id="create-title"
                placeholder="Résumé court du problème"
                value={newIncident.title}
                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
              />
            </div>

            {/* Category & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-category">Catégorie *</Label>
                <Select
                  value={newIncident.category}
                  onValueChange={(value) => setNewIncident({ ...newIncident, category: value })}
                >
                  <SelectTrigger id="create-category">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INCIDENT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-priority">Priorité *</Label>
                <Select
                  value={newIncident.priority}
                  onValueChange={(value) => setNewIncident({ ...newIncident, priority: value })}
                >
                  <SelectTrigger id="create-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(INCIDENT_PRIORITIES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="create-description">Description *</Label>
              <Textarea
                id="create-description"
                placeholder="Description détaillée du problème"
                value={newIncident.description}
                onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                rows={4}
              />
            </div>

            {/* Reproduction steps */}
            <div className="space-y-2">
              <Label htmlFor="create-repro">Étapes de reproduction</Label>
              <Textarea
                id="create-repro"
                placeholder="1. Étape 1&#10;2. Étape 2..."
                value={newIncident.reproduction_steps}
                onChange={(e) => setNewIncident({ ...newIncident, reproduction_steps: e.target.value })}
                rows={3}
              />
            </div>

            {/* Expected & Observed */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-expected">Résultat attendu</Label>
                <Textarea
                  id="create-expected"
                  placeholder="Ce qui devrait se passer"
                  value={newIncident.expected_result}
                  onChange={(e) => setNewIncident({ ...newIncident, expected_result: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-observed">Résultat observé</Label>
                <Textarea
                  id="create-observed"
                  placeholder="Ce qui se passe réellement"
                  value={newIncident.observed_result}
                  onChange={(e) => setNewIncident({ ...newIncident, observed_result: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate}>
              Créer l'incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail/Edit Modal */}
      {selectedIncident && (
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedIncident.title}</DialogTitle>
              <DialogDescription>
                Incident #{selectedIncident.id.substring(0, 8)} • {selectedIncident.category}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Détails</TabsTrigger>
                <TabsTrigger value="admin">Gestion</TabsTrigger>
                <TabsTrigger value="history">Historique</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                {/* Description */}
                <div className="space-y-2">
                  <Label className="font-medium">Description</Label>
                  <p className="text-sm p-3 bg-muted rounded-md whitespace-pre-wrap">
                    {selectedIncident.description}
                  </p>
                </div>

                {/* Reproduction steps */}
                {selectedIncident.reproduction_steps && (
                  <div className="space-y-2">
                    <Label className="font-medium">Étapes de reproduction</Label>
                    <p className="text-sm p-3 bg-muted rounded-md whitespace-pre-wrap">
                      {selectedIncident.reproduction_steps}
                    </p>
                  </div>
                )}

                {/* Expected/Observed */}
                {(selectedIncident.expected_result || selectedIncident.observed_result) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedIncident.expected_result && (
                      <div className="space-y-2">
                        <Label className="font-medium">Résultat attendu</Label>
                        <p className="text-sm p-3 bg-muted rounded-md">
                          {selectedIncident.expected_result}
                        </p>
                      </div>
                    )}
                    {selectedIncident.observed_result && (
                      <div className="space-y-2">
                        <Label className="font-medium">Résultat observé</Label>
                        <p className="text-sm p-3 bg-muted rounded-md">
                          {selectedIncident.observed_result}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Context */}
                <div className="space-y-2">
                  <Label className="font-medium">Contexte technique</Label>
                  <div className="text-xs space-y-1 p-3 bg-muted rounded-md">
                    <p><strong>Priorité:</strong> {INCIDENT_PRIORITIES[selectedIncident.priority]?.label}</p>
                    <p><strong>Origine:</strong> {selectedIncident.origin}</p>
                    {selectedIncident.affected_user && <p><strong>Utilisateur affecté:</strong> {selectedIncident.affected_user}</p>}
                    {selectedIncident.browser_info && <p><strong>Navigateur:</strong> {selectedIncident.browser_info}</p>}
                    {selectedIncident.device_info && <p><strong>Appareil:</strong> {selectedIncident.device_info}</p>}
                    <p><strong>Version app:</strong> {selectedIncident.app_version}</p>
                  </div>
                </div>

                {/* Logs */}
                {selectedIncident.logs && (
                  <div className="space-y-2">
                    <Label className="font-medium">Logs</Label>
                    <pre className="text-xs p-3 bg-muted rounded-md overflow-x-auto">
                      {selectedIncident.logs}
                    </pre>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="admin" className="space-y-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label htmlFor="update-status">Statut</Label>
                  <Select
                    value={updateData.status}
                    onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
                  >
                    <SelectTrigger id="update-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(INCIDENT_STATUSES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status comment */}
                <div className="space-y-2">
                  <Label htmlFor="status-comment">Commentaire de changement</Label>
                  <Input
                    id="status-comment"
                    placeholder="Raison du changement de statut..."
                    value={updateData.status_comment}
                    onChange={(e) => setUpdateData({ ...updateData, status_comment: e.target.value })}
                  />
                </div>

                {/* Admin notes */}
                <div className="space-y-2">
                  <Label htmlFor="admin-notes">Notes administrateur</Label>
                  <Textarea
                    id="admin-notes"
                    placeholder="Notes internes, actions prises, RCA..."
                    value={updateData.admin_notes}
                    onChange={(e) => setUpdateData({ ...updateData, admin_notes: e.target.value })}
                    rows={6}
                  />
                </div>
              </TabsContent>

              <TabsContent value="history" className="space-y-4">
                <div className="space-y-3">
                  {selectedIncident.status_history.map((entry, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <Badge variant={INCIDENT_STATUSES[entry.status]?.color || 'default'}>
                          {INCIDENT_STATUSES[entry.status]?.label || entry.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(entry.changed_at)}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{entry.changed_by}</p>
                      <p className="text-sm text-muted-foreground mt-1">{entry.comment}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
                Fermer
              </Button>
              <Button onClick={handleUpdate}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default IncidentManager;
